import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, Gift, Sparkles, DollarSign, Zap, ShieldCheck, Heart,
  Repeat2, BadgePercent, Users,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import NoFeeLogo from '../components/NoFeeLogo';
import { LanguageProvider } from '../context/LanguageContext';

/* ─────────────────────────────────────────────────────────────────────
 * /ref/friend/:code — Friend referral landing page.
 *
 * iter88 (Jun 2026): rebuilt — removed the legacy Ambassador / Bronze /
 * tier badge UI that was confusing visitors with concepts that no
 * longer exist in the new Gift Card Credits economy. The page is now
 * a focused conversion funnel:
 *
 *   1. Personal invite header — "<First> thinks you'll love NoFee."
 *      Surfaces the +$0.50 Gift Card Credit reward both sides get.
 *   2. Twin CTAs — Create Free Account (primary) / Sign In.
 *   3. Marketing benefits grid below — the same value-prop content
 *      that lives on the public Rewards landing page (/rewards), so
 *      a new visitor understands what NoFee actually offers before
 *      they sign up.
 *
 * No tier logic, no Bronze/Silver/Gold badges, no Ambassador wording
 * anywhere on the page.
 * ─────────────────────────────────────────────────────────────────── */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  {
    Icon: DollarSign,
    title: 'Pay Less For Your Food',
    desc: "Many restaurants offer better prices on NoFee because they don't pay high marketplace commissions.",
    tone: 'border-yellow-500/40 bg-yellow-500/[0.06]',
    accent: 'text-yellow-300',
  },
  {
    Icon: Zap,
    title: 'Fast by NoFee',
    desc: 'Delivered on time. Verified by real customers. See which restaurants consistently deliver within their promised window.',
    tone: 'border-yellow-500/40 bg-yellow-500/[0.06]',
    accent: 'text-yellow-300',
  },
  {
    Icon: Gift,
    title: 'Gift Card Credits',
    desc: 'Earn $0.50 in credits for every friend referral, $1.00 per social share. Redeem on any participating restaurant.',
    tone: 'border-yellow-500/40 bg-yellow-500/[0.06]',
    accent: 'text-yellow-300',
  },
  {
    Icon: Heart,
    title: 'Save Your Favorites',
    desc: 'One-tap favorites across desktop, tablet and mobile. Always linked to your account, never your browser.',
    tone: 'border-white/10 bg-white/[0.02]',
    accent: 'text-rose-300',
  },
  {
    Icon: Repeat2,
    title: 'One-Tap Reorder',
    desc: 'Your complete order history with modifiers, notes and totals. Tap once to rebuild your cart at any restaurant.',
    tone: 'border-white/10 bg-white/[0.02]',
    accent: 'text-emerald-300',
  },
  {
    Icon: ShieldCheck,
    title: 'No Hidden Fees',
    desc: 'Transparent pricing — sales tax shown line-by-line, no surprise marketplace markups. What you see is what you pay.',
    tone: 'border-white/10 bg-white/[0.02]',
    accent: 'text-sky-300',
  },
  {
    Icon: BadgePercent,
    title: 'Restaurant Promotions',
    desc: 'Exclusive deals from local restaurants — only on NoFee, no commission cuts forcing them to raise prices.',
    tone: 'border-white/10 bg-white/[0.02]',
    accent: 'text-violet-300',
  },
  {
    Icon: Users,
    title: 'Refer & Earn',
    desc: 'Get $0.50 every time a friend joins, $30.00 every time a restaurant joins NoFee through your invite link.',
    tone: 'border-white/10 bg-white/[0.02]',
    accent: 'text-amber-300',
  },
];

const Inner = () => {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const upper = code.toUpperCase();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const refRes = await axios.get(`${API}/r/${encodeURIComponent(upper)}/preview`);
        if (cancelled) return;
        if (refRes.data?.valid) {
          setData(refRes.data);
        } else {
          // Legacy QR codes used /r/:slug for public menus — keep
          // those redirects working before declaring the code invalid.
          try {
            await axios.get(`${API}/restaurants/${encodeURIComponent(code)}`);
            navigate(`/menu/${code}`, { replace: true });
            return;
          } catch { /* not a restaurant either */ }
          setData(refRes.data);
        }
      } catch {
        if (!cancelled) setData({ valid: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [upper, code, navigate]);

  if (loading) {
    return (
      <div className="pt-32 text-center min-h-screen bg-black">
        <SEO title="Friend Invite" description="Personalized referral invite for NoFee — claim your gift card credit." path={`/r/${code || ''}`} noindex />
        <div className="inline-block w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ─── Invalid code → push to the plain customer signup ───
  if (!data?.valid) {
    return (
      <div className="min-h-screen bg-black text-white pt-24 pb-16">
        <Navbar />
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center mx-auto mb-4">
            <Gift className="w-6 h-6 text-yellow-300" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Invitation not recognized</h1>
          <p className="mt-2 text-[14px] text-white/60">
            We couldn&apos;t find a referral for code{' '}
            <code className="px-1.5 py-0.5 rounded bg-white/[0.05] text-yellow-200">{upper}</code>.
          </p>
          <p className="mt-1 text-[13px] text-white/45">You can still create your account below.</p>
          <Link
            to="/customer"
            className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold"
            data-testid="referral-invalid-cta"
          >
            Create your free account <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const firstName = (data.inviter_name || 'A friend').split(' ')[0];
  const inviterReward = data.inviter_reward_label || '$0.50 Gift Card Credit';
  const friendReward = data.friend_reward_label || '$0.50 Gift Card Credit';
  const signupHref = `/customer?ref=${encodeURIComponent(upper)}`;
  const loginHref = `/customer/login?ref=${encodeURIComponent(upper)}`;

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title={`${firstName} invited you to NoFee — Claim ${friendReward}`}
        description={`${firstName} thinks you'll love NoFee — 0% commission food ordering. Sign up with this referral and unlock ${friendReward} on your first order.`}
        path={`/r/${upper}`}
        noindex
      />
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4">

          {/* ─── Brand-first hero (iter105) ───
              Official NoFee logo + platform hero copy, so the referral
              experience mirrors the live homepage identity. The personal
              "{firstName} invited you" pill comes right after — keeping
              the social-proof angle WITHOUT hiding the brand. */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center">
              <NoFeeLogo height={64} />
            </div>

            <div className="mt-5 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-[11px] uppercase tracking-[0.2em] font-bold text-yellow-300">
              <Sparkles className="w-3.5 h-3.5" /> {firstName} invited you
            </div>

            <h1 className="mt-5 text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
              <span className="text-yellow-300">0%</span> Food App Fees.<br />
              Keep More Profit.<br />
              Get Paid <span className="text-yellow-300">Directly</span>.
            </h1>

            <p className="mt-5 text-[15px] sm:text-[16px] text-white/65 max-w-xl mx-auto leading-relaxed">
              Order direct from your favorite local restaurants — better prices, real on-time delivery, and Gift Card Credits in your pocket.
            </p>

            {/* Reward pill — both sides win */}
            <div
              data-testid="referral-reward-pill"
              className="mt-6 inline-flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 rounded-2xl border border-yellow-500/30 bg-gradient-to-br from-yellow-500/[0.08] to-yellow-500/[0.02]"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-yellow-500/20 border border-yellow-500/40 flex items-center justify-center shrink-0">
                  <Gift className="w-4 h-4 text-yellow-300" />
                </div>
                <div className="text-left">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-yellow-300/85 font-bold">You get</div>
                  <div className="text-[13.5px] font-extrabold text-white">{friendReward}</div>
                </div>
              </div>
              <div className="hidden sm:block w-px h-9 bg-white/10" />
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/15 flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 text-yellow-300/75" />
                </div>
                <div className="text-left">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold">{firstName} gets</div>
                  <div className="text-[13.5px] font-extrabold text-white">{inviterReward}</div>
                </div>
              </div>
            </div>

            {/* Twin CTAs */}
            <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2.5 max-w-md mx-auto">
              <Link
                to={signupHref}
                data-testid="referral-create-account"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[14px]"
              >
                Create Free Account <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to={loginHref}
                data-testid="referral-sign-in"
                className="flex-1 inline-flex items-center justify-center px-5 py-3.5 rounded-xl border border-white/15 hover:border-yellow-400/50 hover:bg-yellow-500/[0.05] text-white font-bold text-[14px] transition-colors"
              >
                Sign In
              </Link>
            </div>
            <p className="mt-3 text-[11.5px] text-white/45">
              Takes under 30 seconds · no credit card needed
            </p>
          </div>

          {/* ─── Marketing benefits grid ─── */}
          <section
            data-testid="referral-benefits"
            className="mt-14 sm:mt-20 pt-10 sm:pt-14 border-t border-white/10"
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] uppercase tracking-[0.22em] font-bold text-white/65">
                Why NoFee
              </div>
              <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
                What you get when you join
              </h2>
              <p className="mt-2 text-[13.5px] sm:text-[14.5px] text-white/55 max-w-xl mx-auto">
                We built NoFee so restaurants keep more of their revenue — and customers get better prices, faster delivery, and real rewards.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {BENEFITS.map(({ Icon, title, desc, tone, accent }) => (
                <div
                  key={title}
                  data-testid={`referral-benefit-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className={`p-4 rounded-2xl border ${tone} hover:border-yellow-400/40 transition-colors`}
                >
                  <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-3">
                    <Icon className={`w-4.5 h-4.5 ${accent}`} />
                  </div>
                  <h3 className="text-[13.5px] font-extrabold text-white tracking-tight">{title}</h3>
                  <p className="mt-1 text-[12.5px] text-white/60 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            {/* Bottom-of-page repeated CTA so the visitor doesn't have
                to scroll back up after reading the benefits. */}
            <div className="mt-10 text-center">
              <Link
                to={signupHref}
                data-testid="referral-create-account-bottom"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[14px]"
              >
                Claim my {friendReward} <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="mt-2 text-[12px] text-white/45">
                Credits land in your account the moment you sign up.
              </p>
            </div>
          </section>
        </div>
      </div>
      <FooterSection />
    </div>
  );
};

export default function ReferralLanding() {
  return (
    <LanguageProvider>
      <Inner />
    </LanguageProvider>
  );
}
